/**
 * BYOC — Bring Your Own Code.
 *
 * Self-service onboarding for citizen-built solutions on Accounting Central.
 * A user uploads what they built; the platform works out how to deploy it and
 * hands back a Midway-authenticated link (UI apps) or a runnable surface
 * (Python scripts).
 *
 * This folder is intentionally self-contained: BYOC is its own service, so it
 * owns its route content, its own constants and its own api layer. It renders
 * inside the existing app chrome (shared TopNav + side nav) and sits as a peer
 * of Task Management in the side nav, not nested under it.
 *
 * Design docs: "BYOC — Bring Your Own Code" (framework + user flow) and
 * "Dynamic Assets — Data Flow and Refresh Contract", both in the UI team's
 * SharePoint space under ATEAM - UI/AI/Scaling AI solutions.
 */

export const BYOC_ROUTE = "/byoc";
export const BYOC_NAV_LABEL = "BYOC";

/** Upload history for one solution. Its own page, so the URL is shareable. */
export const getByocHistoryRoute = (codeRefId) => `${BYOC_ROUTE}/${codeRefId}`;

/* ---- Stage gate ----
 * BYOC is beta-only while it is being built. The CDK stack is already guarded
 * the same way, so in prod there is no API, no bucket and no distribution —
 * the page would render a dead end. Gating the nav AND the route means neither
 * a click nor a pasted URL can reach it.
 *
 * Listed as an allowlist rather than "not prod": a new stage appearing should
 * default to hidden, not to exposed.
 */
// Stages the BYOC page is shown on.
//
// This is deliberately NOT identical to KEYSTONE_ENABLED_STAGES in the CDK
// package, and the difference is `dev`.
//
// `dev` is the stage token the local dev server substitutes for beta
// (webpack.config.js), and its dev config points at the BETA account and beta
// Cognito. So a developer on localhost runs as stage `dev` while talking to
// beta's Keystone API — there is no `dev` backend and there does not need to
// be. Dropping `dev` from this list does not prevent a dead end; it just hides
// BYOC from everyone developing it.
//
// The rule the two lists actually share: a DEPLOYED stage must appear in both,
// or the page ships with no backend. `beta` and `alpha` satisfy that. `dev` is
// not a deployed stage at all.
export const BYOC_ENABLED_STAGES = ["beta", "dev", "alpha"];

export const isByocEnabled = (stage) =>
  BYOC_ENABLED_STAGES.includes(String(stage || "").toLowerCase());

/* ---- Payload families ----
 * Which family a payload belongs to is determined by the platform, not asked
 * of the user. It decides the deployment target and how long onboarding takes.
 */
export const PAYLOAD_FAMILY = {
  STATIC_UI: "STATIC_UI",
  SERVER_UI: "SERVER_UI",
  SCRIPT: "SCRIPT",
};

export const PAYLOAD_FAMILY_LABEL = {
  [PAYLOAD_FAMILY.STATIC_UI]: "HTML",
  [PAYLOAD_FAMILY.SERVER_UI]: "Streamlit",
  [PAYLOAD_FAMILY.SCRIPT]: "Script",
};

/** Scripts go through a code review, so they take days rather than minutes. */
export const FAMILY_NEEDS_REVIEW = {
  [PAYLOAD_FAMILY.STATIC_UI]: false,
  [PAYLOAD_FAMILY.SERVER_UI]: false,
  [PAYLOAD_FAMILY.SCRIPT]: true,
};

/** Only UI families can carry a refreshable data folder. */
export const FAMILY_SUPPORTS_DYNAMIC_DATA = {
  [PAYLOAD_FAMILY.STATIC_UI]: true,
  [PAYLOAD_FAMILY.SERVER_UI]: true,
  [PAYLOAD_FAMILY.SCRIPT]: false,
};

/* ---- Lifecycle status ----
 * The registry column `status` on acc_central."byoc.code_info", using the same
 * enum the portal's normal apps use (APP_STATUSES in Templates/Constants.js),
 * stored as these mixed-case strings. A BYOC solution and a portal app answer
 * "what state is this in?" the same way, which matters because the two end up
 * in the same views.
 *
 * This is deliberately coarse. It says whether something is live, not where in
 * the pipeline it is — that is SOLUTION_STATUS below.
 */
export const LIFECYCLE_STATUS = {
  WIP: "WIP",
  ACTIVE: "Active",
  DEPRECATED: "Deprecated",
};

/** New records start at WIP; the apps' form disables the other two at creation. */
export const INITIAL_LIFECYCLE_STATUS = LIFECYCLE_STATUS.WIP;

/**
 * Allowed transitions, matching the rule the app forms enforce: something that
 * has been live is never "in progress" again, so Active and Deprecated cannot
 * return to WIP. A change to a live solution is a new upload against the same
 * row, not a reversion of its state.
 */
export const LIFECYCLE_TRANSITIONS = {
  [LIFECYCLE_STATUS.WIP]: [LIFECYCLE_STATUS.ACTIVE, LIFECYCLE_STATUS.DEPRECATED],
  [LIFECYCLE_STATUS.ACTIVE]: [LIFECYCLE_STATUS.DEPRECATED],
  [LIFECYCLE_STATUS.DEPRECATED]: [],
};

export const canTransitionLifecycle = (from, to) => {
  if (from === to) return true;
  return (LIFECYCLE_TRANSITIONS[from] || []).includes(to);
};

/** Deprecated solutions are hidden from the default list, not deleted. */
export const isRetired = (solution) =>
  Boolean(solution) && solution.status === LIFECYCLE_STATUS.DEPRECATED;

/* ---- Pipeline state ----
 * Finer-grained than the lifecycle status, and not persisted on the registry
 * row: where a WIP solution actually is. A row with status WIP can be running
 * checks, held by a finding, or waiting on a code review, and the user needs
 * to be told which.
 */
export const SOLUTION_STATUS = {
  PROCESSING: "PROCESSING",
  IN_REVIEW: "IN_REVIEW",
  CHECKS_FAILED: "CHECKS_FAILED",
  LIVE: "LIVE",
  OWNER_REQUIRED: "OWNER_REQUIRED",
};

/**
 * Status as shown in the list. `LIVE` splits on whether dynamic data is on,
 * which is why this is a function rather than a lookup table.
 */
export const getStatusDisplay = (solution) => {
  if (!solution) return { label: "—", type: "pending" };
  if (isRetired(solution)) return { label: "Deprecated", type: "stopped" };
  switch (solution.pipelineState) {
    case SOLUTION_STATUS.LIVE:
      return solution.dynamicDataEnabled
        ? { label: "Live · data", type: "success" }
        : { label: "Live", type: "success" };
    case SOLUTION_STATUS.IN_REVIEW:
      return { label: "In review", type: "in-progress" };
    case SOLUTION_STATUS.CHECKS_FAILED:
      return { label: "Checks failed", type: "error" };
    case SOLUTION_STATUS.OWNER_REQUIRED:
      return { label: "Owner", type: "warning" };
    case SOLUTION_STATUS.PROCESSING:
      return { label: "Processing", type: "in-progress" };
    default:
      return { label: "—", type: "pending" };
  }
};

/** A solution is only openable once it is actually serving and not retired. */
export const isOpenable = (solution) =>
  Boolean(solution) &&
  solution.status === LIFECYCLE_STATUS.ACTIVE &&
  solution.pipelineState === SOLUTION_STATUS.LIVE &&
  Boolean(solution.link);

/* ---- Name validation ----
 * The name becomes a URL path segment and a storage key segment, so it is
 * constrained to what is safe in both. Mixed case and underscores are allowed
 * because existing hosted apps already use them.
 */
export const NAME_MAX_LENGTH = 63;
export const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,62}$/;
export const NAME_ALLOWED_HINT =
  "Letters, numbers, hyphen, underscore. Becomes part of your link.";

/* ---- Upload form ---- */
/** Sentinel for the "onboard as" dropdown meaning "not an update". */
export const NEW_SOLUTION = "__NEW__";

export const RUN_MODE = {
  INDIVIDUAL: "INDIVIDUAL",
  GROUPED: "GROUPED",
};

export const ACCEPTED_FILE_TYPES = [".zip", ".py"];
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/* ---- Pipeline stages shown on the status panel ----
 * Scripts travel further than UI apps, so the stage list is per family.
 */
export const UI_STAGES = [
  { key: "received", label: "Received" },
  { key: "registered", label: "Registered" },
  { key: "scanned", label: "Scanned" },
  { key: "detected", label: "Detected" },
  { key: "published", label: "Publishing" },
  { key: "notified", label: "Sending link" },
];

export const SCRIPT_STAGES = [
  { key: "received", label: "Received" },
  { key: "registered", label: "Registered" },
  { key: "scanned", label: "Scanned" },
  { key: "built", label: "Built" },
  { key: "sampleRun", label: "Sample run" },
  { key: "codeReview", label: "Code review" },
  { key: "deployed", label: "Deploying" },
  { key: "surfaceCreated", label: "Creating your runnable surface" },
];

export const getStagesForFamily = (family) =>
  family === PAYLOAD_FAMILY.SCRIPT ? SCRIPT_STAGES : UI_STAGES;

/**
 * Said at the moment a link becomes readable rather than once at signup — a
 * user publishing a report on internal data should know the audience.
 */
export const MIDWAY_AUDIENCE_NOTICE =
  "Anyone at Amazon with Midway can open this link. It is authenticated, not private.";

/* ---- Deploy status, as recorded on a transaction item ---- */
export const DEPLOY_STATUS_DISPLAY = {
  pending: { label: "Pending", type: "pending" },
  in_progress: { label: "Publishing", type: "in-progress" },
  done: { label: "Published", type: "success" },
  failed: { label: "Failed", type: "error" },
};

/**
 * "2026-10-14T04:36:25.391Z" -> "2026-10-14 04:36".
 *
 * Seconds and milliseconds are noise in a history table; the date and minute
 * are what distinguish one upload from another.
 */
export const formatTimestamp = (iso) => {
  if (!iso) return "—";
  const text = String(iso);
  const [date, time] = text.split("T");
  if (!time) return date;
  return `${date} ${time.slice(0, 5)}`;
};
